import {
  FrontendRegistration,
  RegistrationActivity,
  FrontendRegistrationCourse,
  FrontendRegistrationGroup,
} from "@timeedit/registration-schemas";
import cn from "classnames";
import {
  exhaustiveMatchingGuard,
  isDefined,
  Mapping,
  UnsafeRecord,
} from "@timeedit/registration-shared";
import {
  Button,
  Column,
  EditableTable as Table,
  Tag,
} from "@timeedit/ui-components";
import { Divider } from "@timeedit/ui-components/antd";
import { ExpandableConfig } from "@timeedit/ui-components/antd/table/interface";
import dayjs from "dayjs";
import {
  ComponentProps,
  ComponentType,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  NavigateAction,
  TitleOptions as RBCTitleOptions,
  ViewProps,
  ViewStatic,
} from "react-big-calendar";
import { CalendarEvent } from "../../../../utils/events";
import { MultiProgress } from "../../../MultiProgress";
import {
  AllocationControlContext,
  IdContext,
  LoadActionContext,
  RegistrationContext,
  UtilContext,
} from "../../RegistrationContexts";
import { RegistrationOptionsContext } from "../../RegistrationOptions";
import { LoadActions, RegistrationRequiredTranslations } from "../../types";
import {
  createGroupNumbers,
  getGroupName,
  sortEventsOnGroupNumber,
} from "../../utils";
import {
  DateColumn,
  DedicatedColumn,
  ExpandableComponent,
  HiddenColumn,
} from "./ListViewColumns";
import InfoCircleOutlined from "@timeedit/ui-components/icons/InfoCircleOutlined";

type ActivityViewProps = {
  events: CalendarEvent[];
  style?: React.CSSProperties;
};
export function ActivityView(props: ActivityViewProps): React.JSX.Element {
  const {
    translations,
    mapping,
    dedicatedGroupsActions: { presentRelation, presentCategory },
  } = useContext(UtilContext);
  const loadActions = useContext(LoadActionContext);

  const { registration, mode } = useContext(RegistrationContext);
  const { id } = useContext(IdContext);
  const allocationControlActions = useContext(AllocationControlContext);
  const { slots } = useContext(RegistrationOptionsContext);

  const groupIds = useMemo(() => {
    if (!isDefined(id)) {
      return [];
    }
    return registration.activities[id]?.children ?? [];
  }, [id, registration.activities]);

  const groupEvents = props.events?.filter((event) =>
    groupIds.includes(event.data.groupId)
  );

  const sortedEvents = sortEventsOnGroupNumber({
    events: groupEvents ?? [],
    mapping,
    groups: registration.groups,
  });

  const mergedEvents = mergeSameAndLinkedEvents({
    events: sortedEvents,
    registration,
  });

  const groupNumbers = useMemo(
    () =>
      isDefined(groupEvents)
        ? createGroupNumbers({
            events: groupEvents,
            mapping,
            groups: registration.groups,
          })
        : [],
    [groupEvents, registration.groups, mapping]
  );

  const dataSource = createGroupListViewDataSource({
    events: mergedEvents,
    registration,
  });

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  return (
    <div style={props.style}>
      {createTable({
        size: "middle",
        tableLayout: "auto",
        dataSource,
        // Headers and caption are screen reader only.
        caption: <span>{mapping.typename("group")}</span>,
        className: cn("groupstable", `grouptable--${mode}`),
        columns: createColumns(),
        pagination: false,
        expandable: tableExpandable({
          expandedRowKeys,
          mapping,
          registration,
          translations,
          loadActions,
        }),
      })}
    </div>
  );

  function createColumns(): Column<ListDataSource>[] {
    const optionsColumn: Column<ListDataSource> = {
      title: mapping.typename("group"),
      dataIndex: "option",
      key: "option",
      render: (_, { groups, mergedEvents }) => {
        return (
          <div>
            {groups.map((group) => {
              const groupName = getGroupName(group, mapping);
              const groupNumberIndex =
                mergedEvents[0].data.groupNumber ??
                groupNumbers.findIndex(
                  (groupNumber) => groupNumber === groupName
                );

              const colorIndex =
                groupNumberIndex === -1 ? "grey" : groupNumberIndex;

              const text =
                groups.length > 1
                  ? `${mapping.parse(
                      "activityType",
                      group.teObject
                    )} ${groupName}`
                  : groupName;

              const allocatedColor =
                group.allocationStatus === "ALLOCATED_TO_THIS" ||
                group.allocationStatus === "MULTIPLE_ALLOCATIONS"
                  ? "groupbox--allocated"
                  : "";
              return (
                <Tag
                  key={group.id}
                  boxed
                  id={`group-id-${group.id}`}
                  className={`grouplabel grouptag groupbox groupbox--${
                    groups.length > 1 ? "multiple" : "single"
                  } rbc-day-slot rbc-event--${colorIndex} ${allocatedColor}`}
                >
                  {text}
                </Tag>
              );
            })}
          </div>
        );
      },
    };
    const dateColumn: Column<ListDataSource> = {
      title: translations.schedule,
      dataIndex: "day",
      key: "day",
      render: (_, { mergedEvents }) => {
        return (
          <DateColumn
            autoWidth={true}
            translations={translations}
            events={mergedEvents}
          />
        );
      },
    };

    const allocationBarColumn: Column<ListDataSource> = {
      title: translations.allocation,
      dataIndex: "allocation",
      key: "allocation",
      render: (_, { groups }) => {
        // In the teacher view, we just show the progress value from one of the groups.
        // If there are a difference between the groups then we will show it in the issue list that we implement later on.
        return (
          <MultiProgress
            value={groups[0].seats.raw.taken}
            end={groups[0].seats.raw.buffer}
            max={groups[0].seats.raw.total}
            explainValue={translations.allocated}
            explainEnd={translations.BUFFER}
            compact={false}
            formatHelp={() =>
              `(${translations.BUFFER}: ${groups[0].seats.raw.buffer})`
            }
            translations={translations}
          />
        );
      },
    };

    const hiddenColumn: Column<ListDataSource> = {
      title: translations.HIDETRACK,
      dataIndex: "hidden",
      key: "hidden",
      render: (_, { groups }) => {
        const hidden = groups.some((group) => group.hidden);
        return <HiddenColumn hidden={hidden} label={translations.HIDETRACK} />;
      },
    };

    const dedicatedColumn: Column<ListDataSource> = {
      title: translations.DEDICATETRACK,
      dataIndex: "dedicated",
      key: "dedicated",
      render: (_, { groups }) => {
        const dedicated = groups.map((group) => group.dedicated);
        return (
          <DedicatedColumn
            dedicated={dedicated}
            presentCategory={presentCategory}
            presentRelation={presentRelation}
          />
        );
      },
    };

    const controlsColumn: Column<ListDataSource> = {
      title: translations.action,
      dataIndex: "",
      key: "",
      render: (_, { activity, groups, mergedEvents }) => {
        const hasConflict = mergedEvents.some(
          (event) => event.data.hasConflict
        );
        return slots.groupControls({
          hasConflict,
          activity,
          groups,
          registration,
          translations,
          allocationControlActions,
          mode,
          mapping,
        });
      },
    };

    const toggleRow = (key: string) => {
      if (expandedRowKeys.includes(key)) {
        setExpandedRowKeys(
          expandedRowKeys.filter((k) => key.localeCompare(k.toString()))
        );
        return;
      }
      setExpandedRowKeys([...expandedRowKeys, key]);
    };

    const expandColumn: Column<ListDataSource> = {
      title: translations.moreInformation,
      dataIndex: "expand",
      key: "expand",
      render: (_value, record) => {
        const expanded = expandedRowKeys.includes(record.key);
        const css = expanded ? " button-expanded" : "";
        return (
          <Button
            className={"groupiconbox" + css}
            icon={<InfoCircleOutlined alt="Info" className="groupboxicon" />}
            onClick={(_) => toggleRow(record.key)}
            aria-expanded={expanded}
          ></Button>
        );
      },
    };

    // Columns are laid out with CSS grid and the styles need to be updated
    // if the number of columns change for either students or teachers.
    switch (mode) {
      case "student": {
        return [optionsColumn, expandColumn, controlsColumn, dateColumn];
      }
      case "teacher": {
        return [
          optionsColumn,
          hiddenColumn,
          dedicatedColumn,
          allocationBarColumn,
          expandColumn,
          controlsColumn,
          dateColumn,
        ];
      }
      default: {
        return exhaustiveMatchingGuard(mode);
      }
    }
  }
}

type MergeEventsProps = {
  events: CalendarEvent[];
  registration: FrontendRegistration;
};

/**
 * @description Purpose of this function is to group events so they can later be displayed on the same row.
 * @param events - Events to merge
 * @param registration - FrontendRegistration object
 * @returns Merged events with same groupId and if they are linked, into the same array.
 */
export function mergeSameAndLinkedEvents({
  events,
  registration,
}: MergeEventsProps): CalendarEvent[][] {
  return (
    events.reduce<CalendarEvent[][]>((mergedEvents, currentEvent) => {
      const idIndex = mergedEvents.findIndex((event) => {
        const groupId = event[0].data.groupId;
        const currentGroupId = currentEvent.data.groupId;
        return (
          groupId === currentGroupId ||
          registration.groups[currentGroupId]?.links.includes(groupId)
        );
      });
      if (idIndex !== -1) {
        mergedEvents[idIndex].push(currentEvent);
        mergedEvents[idIndex].sort(
          (a, b) => dayjs(a.start).day() - dayjs(b.start).day()
        );
      } else {
        mergedEvents.push([currentEvent]);
      }

      return mergedEvents;
    }, []) ?? []
  );
}

function GroupListView(props: ViewProps<CalendarEvent>): React.JSX.Element {
  return <ActivityView events={props.events ?? []} />;
}

type ListDataSource = {
  course: FrontendRegistrationCourse;
  groups: FrontendRegistrationGroup[];
  activity: RegistrationActivity;
  mergedEvents: CalendarEvent[];
  key: string;
};

interface CreateTableProps<T extends object>
  extends Omit<ComponentProps<typeof Table<T>>, "dataSource" | "columns"> {
  dataSource: T[];
  columns: Column<T>[];
}
function createTable({
  columns,
  dataSource,
  ...props
}: CreateTableProps<ListDataSource>): React.JSX.Element {
  return <Table {...props} columns={columns} dataSource={dataSource} />;
}

interface CreateDataSource {
  registration: FrontendRegistration;
  events: CalendarEvent[][];
}

/**
 * @param registration - FrontendRegistration object
 * @param events - Events that are groups together (linked or same groupId)
 * @returns An array of objects that has grouped course and activity with events and their matching groups.
 */

export function createGroupListViewDataSource({
  registration,
  events,
}: CreateDataSource): ListDataSource[] {
  const indexRecord: UnsafeRecord<string, number> = {};
  return events.reduce((dataSource: ListDataSource[], mergedEvents) => {
    // This is used just to get the activity
    const activityId =
      registration.groups[mergedEvents[0].data.groupId]?.parentId;

    if (!isDefined(activityId)) return dataSource;

    const activity = registration.activities[activityId];

    if (!isDefined(activity)) return dataSource;

    const course = registration.courses[activity.parentId];

    if (!isDefined(course)) return dataSource;

    // The groups on a activity that are included in mergedEvents
    const groups = activity.children.reduce(
      (groups: FrontendRegistrationGroup[], groupId) => {
        const isInMergedEvents = mergedEvents.some(
          (event) => event.data.groupId === groupId
        );
        const group = registration.groups[groupId];
        if (!isDefined(group)) return groups;
        return isInMergedEvents ? [...groups, group] : groups;
      },
      []
    );
    const indexRecordKey = groups.map((group) => group.id).join("-");

    if (!isDefined(indexRecord[indexRecordKey])) {
      indexRecord[indexRecordKey] = 0;
    }
    // key is based on the first element in the merged group
    const key = `${
      mergedEvents[(indexRecord[indexRecordKey] ?? 0) % mergedEvents.length].id
    }`;

    dataSource.push({
      activity,
      groups,
      course,
      mergedEvents,
      key,
    });
    indexRecord[indexRecordKey] = (indexRecord[indexRecordKey] ?? 0) + 1;
    return dataSource;
  }, []);
}
type TabelExpandalbeProps = {
  registration: FrontendRegistration;
  mapping: Mapping;
  translations: RegistrationRequiredTranslations;
  expandedRowKeys: React.Key[];
  loadActions: LoadActions;
};
function tableExpandable({
  registration,
  mapping,
  translations,
  expandedRowKeys,
  loadActions,
}: TabelExpandalbeProps): ExpandableConfig<ListDataSource> {
  return {
    showExpandColumn: false,
    expandedRowKeys,
    expandedRowRender: ({ mergedEvents }: ListDataSource) => {
      return (
        <>
          {...mergedEvents.map((event, index) => {
            const { data } = event;
            return (
              <div key={`tableExpandable-${data.groupId}-${index}`}>
                <ExpandableComponent
                  mapping={mapping}
                  translations={translations}
                  event={event}
                  registration={registration}
                  loadActions={loadActions}
                />
                {index !== mergedEvents.length - 1 && (
                  <Divider className="resboxdivider" />
                )}
              </div>
            );
          })}
        </>
      );
    },
  };
}

GroupListView.navigate = (
  date: Date,
  action: NavigateAction,
  props: unknown
): Date => {
  const parameters = { date, action, props };
  return parameters.date;
};

interface TitleOptions extends Partial<RBCTitleOptions> {
  localizer: ViewProps["localizer"];
  length: ViewProps["length"];
}

GroupListView.title = (
  date: Date,
  { localizer, length }: TitleOptions
): string => {
  const end = localizer.add(date, length, "day");
  return localizer.format({ date, end }, "agendaHeaderFormat");
};

export default GroupListView as ComponentType<ViewProps<CalendarEvent>> &
  ViewStatic;
